import { required, requiredIf } from 'vuelidate/lib/validators';
import { postcode } from '@/services/Validators/PostcodeValidator';
import { cloneDeep, isEmpty, isUndefined, isObject } from 'lodash';
import PropertyStatus from "./PropertyStatus";
import store from '@/store';
const isUndefinedOrEmpty = value => isUndefined(value) || isEmpty(value);

const isFieldVisibleBasedOnValue = (fields, fieldName) => {
  const field = fields[fieldName];
  return !field || !field.value || field.value.value !== 1;
};

export const form = () => cloneDeep({
  masterReference: {
    label: 'Master reference',
    type: 'text',
    value: '',
    columnOptions: {
      md: 12,
    },
  },
  parentUprn: {
    type: 'text',
    label: 'Parent UPRN',
    value: '',
    disabled: true,
  },
  blockNumberParity: {
    label: 'Is the block an odd or even number?',
    type: 'select',
    value: '',
    options: [
      { value: '', label: 'Select option' },
      { value: 'odd', label: 'Odd' },
      { value: 'even', label: 'Even' }
    ],
    trackBy: 'value',
    labelBy: 'label',
  },
  propertyHierarchyType: {
    type: 'text',
    label: 'Hierarchy Level',
    value: '',
    disabled: true,
  },
  unitNumber: {
    label: 'Unit Number',
    type: 'text',
    value: '',
  },
  numberRange: {
    label: 'Number Range',
    type: 'text',
    value: '',
  },
  buildingName: {
    label: 'Building Name',
    type: 'text',
    value: '',
  },
  streetNumber: {
    label: 'Street Number',
    type: 'text',
    value: '',
  },
  street: {
    label: 'Street Name',
    type: 'text',
    value: '',
  },
  town: {
    label: 'Town',
    type: 'text',
    value: '',
  },
  postcode: {
    label: 'Postcode',
    type: 'text',
    value: '',
  },
  addressInfo: {
    label: 'Address Info',
    type: 'textarea',
    value: '',
  },
  tenure: {
    label: 'Tenure',
    type: 'text',
    value: '',
  },
  identifiedRisks: {
    label: 'Identified risks from any other FRA',
    type: 'textarea',
    value: 'None',
    hidden: () => !store.getters.userClientHasSection156Active,
  },
  tradeButtonAccess: {
    label: 'Can you access via a trade button or intercom?',
    type: 'select',
    value: '',
    options: [
      { value: '', label: 'Select option' },
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' }
    ],
    trackBy: 'value',
    labelBy: 'label',
    group: 'External Building Access',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
  },
  tradeButtonInfo: {
    label: 'What time are they available or other information?',
    type: 'textarea',
    value: '',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
    hidden: (fields) => isFieldVisibleBasedOnValue(fields, 'tradeButtonAccess'),
  },
  fireKeyRequired: {
    label: 'Is a Fire Brigade drop switch available?',
    type: 'select',
    value: '',
    options: [
      { value: '', label: 'Select option' },
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' }
    ],
    trackBy: 'value',
    labelBy: 'label',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
  },
  fireKeyTypeId: {
    label: 'What type of key?',
    type: 'select',
    value: '',
    options: [],
    trackBy: 'value',
    labelBy: 'label',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
    hidden: (fields) => isFieldVisibleBasedOnValue(fields, 'fireKeyRequired'),
  },
  fobKeyRequired: {
    label: 'Is a fob or key required?',
    type: 'select',
    value: '',
    options: [
      { value: '', label: 'Select option' },
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' }
    ],
    trackBy: 'value',
    labelBy: 'label',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
    group: 'Internal Building Access',
  },
  keyTypeId: {
    label: 'What type of key?',
    type: 'select',
    value: '',
    options: [],
    trackBy: 'value',
    labelBy: 'label',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
    hidden: (fields) => isFieldVisibleBasedOnValue(fields, 'fobKeyRequired'),
  },
  fobKeyInfo: {
    label: 'Add a description if needed',
    type: 'textarea',
    value: '',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
    hidden: (fields) => isFieldVisibleBasedOnValue(fields, 'fobKeyRequired'),
  },
  keyCodeRequired: {
    label: 'Is a key code required?',
    type: 'select',
    value: '',
    options: [
      { value: '', label: 'Select option' },
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' }
    ],
    trackBy: 'value',
    labelBy: 'label',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
  },
  keyCodeInfo: {
    label: 'What is the code?',
    type: 'text',
    value: '',
    columnOptions: {
      md: 12,
    },
    colMd: 6,
    hidden: (fields) => isFieldVisibleBasedOnValue(fields, 'keyCodeRequired'),
  },
});

export const validators = () => cloneDeep( {
  masterReference: {
    required,
  },
  postcode: {
    required,
    postcode,
  },
  street: {
    required,
  },
  town: {
    required,
  },
  buildingName: {
    requiredIf: requiredIf(function() {
      return isUndefinedOrEmpty(this.$v.fields.numberRange.value.$model)
          && isUndefinedOrEmpty(this.$v.fields.streetNumber.value.$model);
    }),
  },
  numberRange: {
    requiredIf: requiredIf(function() {
      return isUndefinedOrEmpty(this.$v.fields.buildingName.value.$model)
          && isUndefinedOrEmpty(this.$v.fields.streetNumber.value.$model);
    }),
  },
  streetNumber: {
    requiredIf: requiredIf(function() {
      return isUndefinedOrEmpty(this.$v.fields.numberRange.value.$model)
          && isUndefinedOrEmpty(this.$v.fields.buildingName.value.$model);
    }),
  },

  fireKeyTypeId: {
    requiredIf: requiredIf(function() {
      const fireKeyRequired = this.$v.fields.fireKeyRequired && this.$v.fields.fireKeyRequired.value;
      const fireKeyRequiredValue = fireKeyRequired && fireKeyRequired.$model && fireKeyRequired.$model.value;
      return fireKeyRequiredValue === 1;
    })
  },

  keyTypeId: {
    requiredIf: requiredIf(function() {
      const fobKeyRequired = this.$v.fields.fobKeyRequired && this.$v.fields.fobKeyRequired.value;
      const fobKeyRequiredValue = fobKeyRequired && fobKeyRequired.$model && fobKeyRequired.$model.value;
      return fobKeyRequiredValue === 1;
    })
  },

  keyCodeInfo: {
    requiredIf: requiredIf(function() {
      const keyCodeRequired = this.$v.fields.keyCodeRequired && this.$v.fields.keyCodeRequired.value;
      const keyCodeRequiredValue = keyCodeRequired && keyCodeRequired.$model && keyCodeRequired.$model.value;
      return keyCodeRequiredValue === 1;
    }),
  },
});
